import { useMemo } from 'react';
import {
  SEARCH_DEBOUNCE_MS,
  useDebouncedUrlParam,
} from './useDebouncedUrlParam';
import { useTablePaginationState } from './useTablePaginationState';

export { SEARCH_DEBOUNCE_MS };

/** Empty / search-empty copy shape shared by ANZ and World list tables. */
export type RoyaltiesListEmptyCopy = {
  emptyState: {
    description: string;
    title: string;
  };
  searchNoResultsEmpty: {
    description: string;
    icon: 'search';
    title: string;
  };
};

/**
 * URL-backed search/sort/page state and empty-state copy for royalties list
 * tables (ANZ + World). Search uses local input + debounced URL commit
 * (Titles / Clients pattern). Pair with {@link usePaginationBounds} and
 * {@link AppPagination} once the paginated query has resolved.
 *
 * For the search spinner, pair {@link useIsSearchFetching} with the list
 * query's `isFetching` / `isPending` after this hook (list query needs page /
 * search from here first).
 *
 * copy: Region-appropriate empty-state messages. Must be passed explicitly
 * (not optional) to ensure ANZ and World copy cannot disagree.
 */
export const useRoyaltiesTableState = ({
  copy,
  defaultSortBy,
  paginationKey,
  searchKey,
  sortByKey,
}: {
  copy: RoyaltiesListEmptyCopy;
  defaultSortBy: string;
  paginationKey: string;
  searchKey: string;
  sortByKey: string;
}) => {
  const pagination = useTablePaginationState({
    defaultSortBy,
    paginationKey,
    searchKey,
    sortByKey,
  });

  const { search, setSearchAndResetPage } = pagination;

  const {
    debounced: debouncedSearch,
    input: searchInput,
    setInput: setSearchInput,
  } = useDebouncedUrlParam(search, setSearchAndResetPage);

  const { emptyState, searchNoResultsEmpty } = copy;
  const customEmptyTable = useMemo(
    () =>
      search
        ? searchNoResultsEmpty
        : {
            description: emptyState.description,
            icon: 'folderOpen' as const,
            title: emptyState.title,
          },
    [emptyState.description, emptyState.title, search, searchNoResultsEmpty],
  );

  return {
    ...pagination,
    customEmptyTable,
    debouncedSearch,
    searchInput,
    setSearchInput,
  };
};
